import * as echarts from "echarts";
import type { ChartConfig } from "@bi-studio/shared/lib";
import type { ChartData, ChartSize } from "./types";
import {
    getChartInstance,
    setupResize,
    clearContainer,
    resolveColors,
} from "./utils";

// Turkey GeoJSON URL (provinces)
const TURKEY_GEOJSON_URL = "https://raw.githubusercontent.com/alpers/Turkey-Maps-GeoJSON/master/tr-cities.json";

export const renderMapChart = async (
    container: HTMLElement | null,
    data: ChartData,
    config: ChartConfig,
    _size?: ChartSize,
    theme: 'light' | 'dark' = 'light'
): Promise<HTMLElement | null> => {
    if (!container || typeof window === "undefined") return null;

    clearContainer(container);

    // Set container size
    container.style.width = "100%";
    container.style.height = "100%";

    const chartInstance = getChartInstance(container, theme);

    // Register map if not already registered
    if (!echarts.getMap('turkey')) {
        chartInstance.showLoading();
        try {
            const response = await fetch(TURKEY_GEOJSON_URL);
            const geoJson = await response.json();
            echarts.registerMap('turkey', geoJson);
            chartInstance.hideLoading();
        } catch (error) {
            console.error("Failed to load Turkey GeoJSON:", error);
            chartInstance.hideLoading();
            container.innerHTML = `<div style="display: flex; align-items: center; justify-content: center; height: 100%; color: red;">Failed to load map data</div>`;
            return container;
        }
    }

    const regionField = (config as any).regionField || "province";
    const valueField = (config as any).valueField || "value";

    const mapData = data.map(row => ({
        name: String(row[regionField] || ""),
        value: Number(row[valueField]) || 0
    }));

    const values = mapData.map(d => d.value);
    const minVal = (config as any).minValue !== undefined ? (config as any).minValue : (values.length > 0 ? Math.min(...values) : 0);
    const maxVal = (config as any).maxValue !== undefined ? (config as any).maxValue : (values.length > 0 ? Math.max(...values) : 100);

    const colors = resolveColors(config, theme);

    const option: echarts.EChartsOption = {
        tooltip: {
            trigger: 'item',
            formatter: '{b}: {c}'
        },
        visualMap: {
            min: minVal,
            max: maxVal,
            left: 'left',
            top: 'bottom',
            text: ['High', 'Low'],
            calculable: true,
            inRange: {
                color: colors.length > 1 ? colors : ['#e0f3f8', '#004a8b']
            },
            textStyle: {
                color: theme === 'dark' ? '#ccc' : '#333'
            }
        },
        series: [
            {
                name: config.title || 'Turkey Map',
                type: 'map',
                map: 'turkey',
                roam: true,
                emphasis: {
                    label: {
                        show: true,
                        color: theme === 'dark' ? '#fff' : '#000'
                    },
                    itemStyle: {
                        areaColor: theme === 'dark' ? '#444' : '#eee'
                    }
                },
                itemStyle: {
                    borderColor: theme === 'dark' ? '#555' : '#ccc',
                    areaColor: theme === 'dark' ? '#333' : '#f5f5f5'
                },
                data: mapData
            }
        ]
    };

    chartInstance.setOption(option);
    setupResize(container, chartInstance);

    return container;
};
